import { useInfiniteQuery } from "@tanstack/react-query";
import {
  fetchDocumentHistoryPage,
  type DocumentHistoryCursor,
  type DocumentHistoryPage,
} from "@/domains/documents/history/documentHistory";

export const DOCUMENT_HISTORY_QUERY_KEY = "document-history" as const;

interface UseDocumentHistoryOptions {
  buildingId: string;
  orgId: string | undefined;
  projectId: number | null;
  enabled: boolean;
}

/**
 * Whether the scope can be queried at all. A query the gate rejects never runs,
 * so it stays `pending` forever — callers need this to tell "still resolving"
 * from a spinner that can never clear.
 */
export function canFetchDocumentHistory(buildingId: string, orgId: string | undefined): boolean {
  const numericBuildingId = Number(buildingId);
  return !!orgId && Number.isInteger(numericBuildingId) && numericBuildingId > 0;
}

export function useDocumentHistory({
  buildingId,
  orgId,
  projectId,
  enabled,
}: UseDocumentHistoryOptions) {
  const numericBuildingId = Number(buildingId);
  return useInfiniteQuery({
    queryKey: [DOCUMENT_HISTORY_QUERY_KEY, orgId, buildingId, projectId],
    enabled: enabled && canFetchDocumentHistory(buildingId, orgId),
    initialPageParam: null as DocumentHistoryCursor | null,
    queryFn: ({ pageParam }): Promise<DocumentHistoryPage> => {
      if (!orgId) return Promise.resolve({ entries: [] });
      return fetchDocumentHistoryPage(
        {
          buildingId: numericBuildingId,
          orgId,
          projectId,
        },
        pageParam
      );
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    // The dialog stays mounted and only toggles `enabled`, so re-opening it is
    // not a fresh mount. Under the client's global 5-minute staleTime a user
    // who trashes a document and re-opens History would be shown the pre-trash
    // timeline with no way to force a reload — there is no refresh control,
    // only "Load more". Nothing invalidates this key, so the query has to go
    // stale immediately and refetch whenever it is re-enabled.
    staleTime: 0,
  });
}
